import React, { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { NotificationBell } from "@/components/notifications";
import { useMessageNotifications } from '@/hooks/useMessageNotifications';
import { CalendarSyncButton } from "@/components/calendar";
import { LanguageSelector } from "@/components/ui/language-selector";
import { SignOutButton } from "@/components/layout/SignOutButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQuickActionBadges } from "@/hooks/useQuickActionBadges";
import {
  LayoutDashboard, ListTodo, Inbox, FolderOpen, Kanban, Users,
  FileText, Phone, File, Handshake, Settings2,
  CalendarDays, DollarSign, FileStack, GripVertical, CalendarClock,
  PlusCircle, PenTool, BarChart3, Rocket, Monitor, Banknote, Briefcase,
  ChevronUp, ChevronDown, ShieldAlert,
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/staff", icon: LayoutDashboard, en: "Dashboard", fr: "Tableau de bord" },
    { to: "/staff/my-work", icon: Briefcase, en: "My Work", fr: "Mon Travail" },
  { to: "/staff/pipeline", icon: GripVertical, en: "Pipeline by Status", fr: "Pipeline par Statut" },
  { to: "/staff/deals-pipeline", icon: Kanban, en: "Deal Pipeline (Kanban)", fr: "Pipeline Kanban" },
  { to: "/staff/users", icon: Users, en: "Users", fr: "Utilisateurs" },
  { to: "/staff/communications", icon: Phone, en: "Communications", fr: "Communications" },
  { to: "/staff/documents", icon: File, en: "Documents", fr: "Documents" },
  { to: "/staff/partners", icon: Handshake, en: "Partners", fr: "Partenaires" },
  { to: "/staff/verifications", icon: ShieldAlert, en: "Identity Verifications", fr: "Vérifications d'identité" },
  { to: "/staff/operations", icon: Settings2, en: "Operations", fr: "Opérations" },
  { to: "/staff/calendar", icon: CalendarDays, en: "Calendar", fr: "Calendrier" },
  { to: "/staff/finance", icon: DollarSign, en: "Finance", fr: "Finance" },
  { to: "/staff/corporate-docs", icon: FileStack, en: "Corporate Docs", fr: "Documents Corporatifs" },
  { to: "/staff/my-availability", icon: CalendarClock, en: "My Availability", fr: "Ma Disponibilité" },
];

const QUICK_ACTIONS = [
  { to: "/staff/my-work?tab=triage", icon: PlusCircle, en: "New Deal (Seller/Broker)", fr: "Nouveau Deal (Vendeur/Broker)", badgeKey: "triage" as const },
  { to: "/tools/nda-generator", icon: PenTool, en: "Generate NDA (Buyer)", fr: "Générer NDA (Acheteur)", badgeKey: "nda" as const },
  { to: "/staff/ai/market-study", icon: BarChart3, en: "Market Study", fr: "Étude de Marché", badgeKey: null },
{ to: "/staff/ai/strategic-plan", icon: Rocket, en: "Strategic Plan", fr: "Plan Stratégique", badgeKey: null },
  { to: "/listings", icon: Monitor, en: "Update Deal Monitor", fr: "Mettre à jour Deal Monitor", badgeKey: null },
  { to: "/forms/lender-offer", icon: Banknote, en: "Lender Offer Form", fr: "Formulaire Offre Lender", badgeKey: "lenderOffers" as const },
  { to: "/staff/users?tab=partner", icon: Handshake, en: "Manage Partners", fr: "Gérer Partenaires", badgeKey: null },
  { to: "/staff/users?tab=consultant", icon: Users, en: "Manage Consultants", fr: "Gérer Consultants", badgeKey: null },
];

const NavItem: React.FC<{ to: string; label: string; icon?: React.ReactNode; badge?: number }> = ({ to, label, icon, badge }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/staff" && pathname.startsWith(to));
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 font-['Geist',sans-serif] ${
        active
          ? "bg-gradient-to-r from-amber-500/25 to-amber-600/15 text-white border border-amber-400/40 shadow-md shadow-amber-500/10"
          : "bg-white/[0.07] text-gray-200 hover:bg-white/[0.12] hover:text-white border border-white/[0.08] hover:border-white/[0.15] hover:shadow-sm"
      }`}
    >
      {icon}
      <span className="text-sm font-medium flex-1">{label}</span>
      {!!badge && badge > 0 && (
        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {badge}
        </span>
      )}
    </Link>
  );
};

export default function StaffLayout() {
  const { language } = useLanguage();
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const badges = useQuickActionBadges(); useMessageNotifications();

  const totalBadges = badges.triage + badges.nda + badges.lenderOffers;

  return (
    <div className="min-h-dvh font-['Geist',sans-serif] bg-brand-gradient">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 p-4 sticky top-0 h-dvh overflow-y-auto bg-white/5 border-r border-white/10 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-4 px-2">
            <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 w-auto object-contain" />
          </div>

          {/* Quick Actions - Collapsible */}
          <div className="mb-4 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => setQuickActionsOpen(!quickActionsOpen)}
              className="flex items-center justify-between w-full px-4 py-2.5 text-xs text-amber-400 uppercase font-semibold hover:bg-white/5 transition-colors"
            >
              <span>{language === "fr" ? "Actions Rapides" : "Quick Actions"}</span>
              <div className="flex items-center gap-2">
                {!quickActionsOpen && totalBadges > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {totalBadges}
                  </span>
                )}
                {quickActionsOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </button>
            {quickActionsOpen && (
              <div className="space-y-1 px-2 pb-2 max-h-64 overflow-y-auto">
                {QUICK_ACTIONS.map((action) => {
                  const badgeCount = action.badgeKey ? badges[action.badgeKey] : 0;
                  return (
                    <Link
                      key={action.to}
                      to={action.to}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-gray-300 hover:bg-amber-500/15 hover:text-amber-300 transition-all border border-transparent hover:border-amber-400/30"
                    >
                      <action.icon className="w-3.5 h-3.5 text-amber-500/70" />
                      <span className="flex-1">{language === "fr" ? action.fr : action.en}</span>
                      {badgeCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                          {badgeCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <nav className="space-y-1.5">
            {NAV_ITEMS.map((item) => (
              <NavItem
                key={item.to}
                to={item.to}
                label={language === "fr" ? item.fr : item.en}
                icon={<item.icon className="w-4 h-4" />}
                badge={item.to === "/staff/verifications" ? badges.verifications : undefined}
              />
            ))}
          </nav>
        </aside>
        {/* Main */}
        <main className="flex-1 p-6 text-white/95">
          <div className="flex justify-end mb-4 gap-3 [&_button]:border-white/40 [&_button]:text-white [&_button]:bg-white/20 [&_button:hover]:bg-white/30 [&_button:hover]:text-white [&_button]:shadow-sm [&_button]:font-medium">
            <LanguageSelector variant="light" />
            <CalendarSyncButton />
            <NotificationBell />
            <SignOutButton />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
